/**
 * ProseMirror document creation and step application for rich text replay.
 *
 * This module provides utilities for:
 * 1. Creating ProseMirror documents from initial state (plain text or existing content)
 * 2. Applying ProseMirror steps to reconstruct documents from edit history
 * 3. Checkpointing for efficient random access during replay scrubbing
 *
 * For rich text replay, we build an actual ProseMirror document and apply
 * stored steps to recreate the document editing history with full formatting.
 */

import type { ProseMirrorNode, Schema } from "@markeroo/editor";
import { textToProseMirror } from "@markeroo/editor/utils";
import { Step, Mapping, StepMap } from "@tiptap/pm/transform";
import type { ExtensionEditEvent, ProseMirrorStep } from "@markeroo/shared";
import { StepApplicationError } from "./errors";
import type {
  PMDocumentState,
  PMDocumentStateBuilder,
  PMDocumentStateBuilderOptions,
  PMDocumentCheckpoint,
  PMChangeRange,
  MarkJSON,
  ReplayEventType,
} from "../types";

/**
 * Options for creating the initial document.
 */
export interface CreateInitialDocumentOptions {
  /**
   * How to handle newlines in the text.
   *
   * - "single" (default): Entire text as one paragraph with literal newlines.
   *   Best for replay because position mapping is trivial (textPos + 1 = pmPos).
   * - "lines": Split on single newlines, create paragraph per line.
   * - "paragraphs": Split on double newlines (paragraph breaks).
   */
  newlineHandling?: "single" | "lines" | "paragraphs";
}

/**
 * Create a ProseMirror document from initial text.
 *
 * This function converts initial document text (from chunkedSnapshot or existing
 * content) into a ProseMirror document suitable for rich text replay.
 *
 * Behavior:
 * - Empty/null text: Returns an empty document with a single empty paragraph
 * - Plain text: Creates a document with the text content
 * - Newlines: Handled according to the `newlineHandling` option (default: "single")
 *
 * The default "single" strategy is recommended for replay because it provides
 * trivial position mapping: textPosition + 1 = ProseMirror position.
 *
 * @param initialText - The initial text content, or null/undefined for empty document
 * @param schema - The ProseMirror schema to use (must have "doc", "paragraph", "text" nodes)
 * @param options - Configuration options for document creation
 * @returns A valid ProseMirror document node
 *
 * @example
 * ```typescript
 * import { schema } from "@markeroo/editor";
 * import { createInitialDocument } from "@markeroo/replay/core";
 *
 * // Empty document
 * const emptyDoc = createInitialDocument(null, schema);
 *
 * // Document with text (single paragraph, preserves newlines)
 * const doc = createInitialDocument("Hello\nWorld", schema);
 *
 * // Document with multiple paragraphs (split on newlines)
 * const multiPara = createInitialDocument("Para 1\nPara 2", schema, {
 *   newlineHandling: "lines"
 * });
 * ```
 */
export function createInitialDocument(
  initialText: string | null | undefined,
  schema: Schema,
  options?: CreateInitialDocumentOptions
): ProseMirrorNode {
  // Handle null/undefined/empty string: return empty document
  const text = initialText ?? "";

  // Map our option to the textToProseMirror strategy
  const strategy = options?.newlineHandling ?? "single";

  return textToProseMirror(text, schema, { strategy });
}

/**
 * Create an empty ProseMirror document.
 *
 * Convenience function for creating a document with a single empty paragraph.
 * Equivalent to `createInitialDocument(null, schema)`.
 *
 * @param schema - The ProseMirror schema to use
 * @returns An empty ProseMirror document
 */
export function createEmptyDocument(schema: Schema): ProseMirrorNode {
  return createInitialDocument(null, schema);
}

// =============================================================================
// ProseMirror Step Application (Rich Text Replay)
// =============================================================================

/**
 * Default checkpoint interval for ProseMirror replay.
 * Lower than plain text (50 vs 100) because step application is more expensive.
 */
const DEFAULT_PM_CHECKPOINT_INTERVAL = 50;

/**
 * Maximum number of checkpoints to cache (LRU).
 * Prevents unbounded memory growth for very long edit sessions.
 */
const MAX_CHECKPOINT_CACHE_SIZE = 20;

/**
 * Maximum number of events to process.
 * Lower than plain text (500k vs 1M) due to higher memory overhead per step.
 */
const MAX_PM_EVENT_COUNT = 500_000;

/**
 * Apply a single ProseMirror step to a document.
 *
 * @param doc - Current ProseMirror document
 * @param stepJSON - Step to apply (JSON format from event.prosemirrorSteps)
 * @param schema - ProseMirror schema for deserialization
 * @param eventIndex - Event index (for error reporting)
 * @param stepIndex - Step index within event (for error reporting)
 * @returns New document after applying step
 * @throws StepApplicationError if step fails to apply
 */
/**
 * Check if a replaceAround step is just setting attributes on a block node.
 * These steps have a slice with a single node that has the same type as the
 * content being wrapped, and the gap preserves all the inner content.
 */
function isAttributeOnlyReplaceAround(
  doc: ProseMirrorNode,
  stepJSON: ProseMirrorStep
): boolean {
  if (stepJSON.stepType !== "replaceAround") return false;

  const slice = stepJSON.slice as { content?: Array<{ type?: string; attrs?: Record<string, unknown> }> } | undefined;
  if (!slice?.content || slice.content.length !== 1) return false;

  // Check if the slice node is a paragraph/heading with just attrs (no text content)
  const sliceNode = slice.content[0];
  if (!sliceNode.type) return false;

  try {
    const resolved = doc.resolve(stepJSON.from);
    const targetNode = resolved.nodeAfter;
    if (!targetNode || targetNode.type.name !== sliceNode.type) {
      return false;
    }
    const expectedEnd = stepJSON.from + targetNode.nodeSize;
    if (stepJSON.to !== expectedEnd) {
      return false;
    }
  } catch {
    return false;
  }

  // The node should have attrs but no content (just wrapping)
  const hasAttrs = Boolean(sliceNode.attrs && Object.keys(sliceNode.attrs).length > 0);
  const hasNoContent = !("content" in sliceNode) || !sliceNode.content;

  return hasAttrs && hasNoContent;
}

/**
 * Result from applying a step, including position mapping information.
 */
interface StepApplicationResult {
  /** The resulting document after applying the step */
  doc: ProseMirrorNode;
  /** The step map for this step (for position transformation of subsequent steps) */
  stepMap: StepMap;
  /** Whether the step was skipped (true = failed but recoverable) */
  skipped: boolean;
}

function getSkippedStepMap(step: Step | null): StepMap {
  return step ? step.getMap().invert() : StepMap.empty;
}

/**
 * Transform a step's positions using a cumulative mapping.
 * This is used when earlier steps have been skipped, causing position drift.
 *
 * @param stepJSON - The step to transform
 * @param mapping - The cumulative mapping from original to current positions
 * @returns A new step JSON with transformed positions
 */
function transformStepPositions(
  stepJSON: ProseMirrorStep,
  mapping: Mapping
): ProseMirrorStep {
  // If no mapping or empty mapping, return unchanged
  if (mapping.maps.length === 0) {
    return stepJSON;
  }

  const stepType = stepJSON.stepType;

  switch (stepType) {
    case "replace":
    case "replaceAround": {
      // Map from position (bias left - prefer earlier positions on ambiguity)
      const from = mapping.map(stepJSON.from, -1);
      // Map to position (bias right - prefer later positions on ambiguity)
      const to = mapping.map(stepJSON.to, 1);

      const transformed: ProseMirrorStep = { ...stepJSON, from, to };

      // ReplaceAroundStep has additional gapFrom/gapTo positions
      if (stepType === "replaceAround") {
        if (stepJSON.gapFrom !== undefined) {
          (transformed as Record<string, unknown>).gapFrom = mapping.map(
            stepJSON.gapFrom as number,
            -1
          );
        }
        if (stepJSON.gapTo !== undefined) {
          (transformed as Record<string, unknown>).gapTo = mapping.map(
            stepJSON.gapTo as number,
            1
          );
        }
      }

      return transformed;
    }

    case "addMark":
    case "removeMark": {
      return {
        ...stepJSON,
        from: mapping.map(stepJSON.from, -1),
        to: mapping.map(stepJSON.to, 1),
      };
    }

    default:
      // Unknown step type - return unchanged
      return stepJSON;
  }
}

/**
 * Check if a replaceAround step is a node type change (e.g., paragraph -> heading).
 * These steps are used for input rule conversions like typing "# " to create a heading.
 * They can be safely skipped if they fail during replay - the text content remains,
 * just without the formatting change.
 */
function isNodeTypeChangeReplaceAround(stepJSON: ProseMirrorStep): boolean {
  if (stepJSON.stepType !== "replaceAround") return false;

  const slice = stepJSON.slice as { content?: Array<{ type?: string }> } | undefined;
  if (!slice?.content || slice.content.length !== 1) return false;

  // Check if the slice contains a block-level node type
  // Note: ProseMirror uses snake_case for node types (bullet_list, ordered_list, etc.)
  const sliceNode = slice.content[0];
  const blockTypes = ["heading", "bullet_list", "ordered_list", "blockquote", "code_block"];
  return blockTypes.includes(sliceNode.type ?? "");
}

/**
 * Try to adjust a replaceAround step to work with the current document structure.
 * If the gap range spans node boundaries, try to constrain it to the first node.
 */
function adjustReplaceAroundStep(
  doc: ProseMirrorNode,
  stepJSON: ProseMirrorStep
): ProseMirrorStep | null {
  const from = stepJSON.from;
  const gapTo = stepJSON.gapTo as number | undefined;
  if (gapTo === undefined) return null;

  let nodeStart = 0;
  let nodeEnd = 0;

  try {
    const resolved = doc.resolve(from);
    const targetNode = resolved.nodeAfter;
    if (!targetNode) return null;
    nodeStart = resolved.pos;
    nodeEnd = nodeStart + targetNode.nodeSize;
  } catch {
    return null;
  }

  // Check if the gap extends beyond this node
  const textStart = nodeStart + 1; // Position after <p>
  const textEnd = nodeEnd - 1; // Position before </p>

  if (gapTo <= textEnd) {
    // Gap is already within the node, no adjustment needed
    return stepJSON;
  }

  // Adjust the step to work on just this node
  // from should be the start of the node (nodeStart)
  // to should be the end of the node (nodeEnd)
  // gapFrom should be just inside the node (textStart)
  // gapTo should be at the end of the node's content (textEnd)
  const adjustedStep = {
    ...stepJSON,
    from: nodeStart,
    to: nodeEnd,
    gapFrom: textStart,
    gapTo: textEnd,
  };

  return adjustedStep;
}

function applyPMStep(
  doc: ProseMirrorNode,
  stepJSON: ProseMirrorStep,
  schema: Schema,
  eventIndex: number,
  stepIndex: number
): StepApplicationResult {

  let step: Step | null = null;

  try {
    // Strip the 'structure' flag from ReplaceAroundStep before applying.
    // The 'structure' flag is a collaborative editing safety mechanism that
    // checks if content between gap positions would be overwritten. During
    // sequential replay from scratch, this check can fail even for valid steps
    // because the document state may differ slightly from when the step was
    // originally captured. Since we're not doing collaborative rebasing, we
    // can safely ignore this flag.
    // See: https://discuss.prosemirror.net/t/confused-about-the-structure-option-on-replacestep-replacearoundstep/1457
    let stepToApply = { ...stepJSON };
    if (stepToApply.structure === true) {
      delete stepToApply.structure;
    }

    // For replaceAround steps that just set attributes (like blockKey),
    // adjust the gap range if it extends beyond the target node's boundaries.
    // This handles cases where the step was captured with positions that
    // don't match our replayed document structure.
    const isAttrOnly = isAttributeOnlyReplaceAround(doc, stepToApply);
    if (isAttrOnly) {
      const adjusted = adjustReplaceAroundStep(doc, stepToApply);
      if (adjusted) {
        stepToApply = adjusted;
      }
    }

    // Deserialize step from JSON
    step = Step.fromJSON(schema, stepToApply);

    // Apply step to document
    const result = step.apply(doc);

    // Check if step failed
    if (result.failed) {
      // For attribute-only replaceAround steps, we can safely skip them
      // if they fail. These steps just set internal attributes (like blockKey)
      // that don't affect the visual content of the document.
      if (isAttrOnly) {
        // Log warning with context for debugging
        console.warn("[replay] Skipping failed attribute-only replaceAround step", {
          eventIndex,
          stepIndex,
          failure: result.failed,
          stepType: stepJSON.stepType,
          from: stepJSON.from,
          to: stepJSON.to,
          gapFrom: stepJSON.gapFrom,
          gapTo: stepJSON.gapTo,
          docNodeSize: doc.nodeSize,
          docChildCount: doc.childCount,
        });
        return { doc, stepMap: getSkippedStepMap(step), skipped: true };
      }

      // For node type change steps (heading, list, etc.), we can also skip
      // them if they fail. The text content is preserved, just without the
      // formatting change. This handles position drift issues where the step
      // positions don't match the replayed document state.
      const isNodeTypeChange = isNodeTypeChangeReplaceAround(stepJSON);
      if (isNodeTypeChange) {
        console.warn("[replay] Skipping failed node type change replaceAround step", {
          eventIndex,
          stepIndex,
          failure: result.failed,
          stepType: stepJSON.stepType,
          sliceType: (stepJSON.slice as { content?: Array<{ type?: string }> })?.content?.[0]?.type,
          from: stepJSON.from,
          to: stepJSON.to,
          docNodeSize: doc.nodeSize,
        });
        return { doc, stepMap: getSkippedStepMap(step), skipped: true };
      }

      // Handle steps that fail due to structural mismatches (e.g., inserting
      // list items when the list structure doesn't exist because an earlier
      // list creation step was skipped)
      const failedMessage = result.failed;
      if (
        failedMessage.includes("Inserted content deeper than insertion position") ||
        failedMessage.includes("Content does not fit in gap") ||
        failedMessage.includes("Invalid content for node") ||
        failedMessage.includes("Cannot join")
      ) {
        console.warn("[replay] Skipping step due to structural mismatch", {
          eventIndex,
          stepIndex,
          failure: failedMessage,
          stepType: stepJSON.stepType,
          from: stepJSON.from,
          to: stepJSON.to,
          docNodeSize: doc.nodeSize,
        });
        return { doc, stepMap: getSkippedStepMap(step), skipped: true };
      }

      throw new StepApplicationError({
        eventIndex,
        stepIndex,
        stepFailure: result.failed,
        documentStateJSON: doc.toJSON() as Record<string, unknown>,
        failedStepJSON: stepJSON,
      });
    }

    // Ensure we have a document (should always be true if !result.failed)
    if (!result.doc) {
      throw new StepApplicationError({
        eventIndex,
        stepIndex,
        stepFailure: "Step succeeded but produced no document",
        documentStateJSON: doc.toJSON() as Record<string, unknown>,
        failedStepJSON: stepJSON,
      });
    }

    // Return the new document with the step's position map
    return { doc: result.doc, stepMap: step.getMap(), skipped: false };
  } catch (error) {
    // Re-throw StepApplicationError as-is
    if (error instanceof StepApplicationError) {
      throw error;
    }

    // For node type change steps (heading, list, etc.), we can skip
    // them if deserialization or application fails. The text content
    // is preserved, just without the formatting change.
    const isNodeTypeChange = isNodeTypeChangeReplaceAround(stepJSON);
    if (isNodeTypeChange) {
      console.warn("[replay] Skipping failed node type change replaceAround step (error during parse/apply)", {
        eventIndex,
        stepIndex,
        error: error instanceof Error ? error.message : String(error),
        stepType: stepJSON.stepType,
        sliceType: (stepJSON.slice as { content?: Array<{ type?: string }> })?.content?.[0]?.type,
        from: stepJSON.from,
        to: stepJSON.to,
        gapFrom: stepJSON.gapFrom,
        gapTo: stepJSON.gapTo,
        insert: stepJSON.insert,
        docNodeSize: doc.nodeSize,
        stepJSON: JSON.stringify(stepJSON).slice(0, 500),
      });
      return { doc, stepMap: getSkippedStepMap(step), skipped: true };
    }

    // For attribute-only replaceAround steps, also skip on failure
    const isAttrOnly = isAttributeOnlyReplaceAround(doc, stepJSON);
    if (isAttrOnly) {
      console.warn("[replay] Skipping failed attribute-only replaceAround step (error during parse/apply)", {
        eventIndex,
        stepIndex,
        error: error instanceof Error ? error.message : String(error),
        stepType: stepJSON.stepType,
        from: stepJSON.from,
        to: stepJSON.to,
      });
      return { doc, stepMap: getSkippedStepMap(step), skipped: true };
    }

    // Handle position out of range errors gracefully - these happen when
    // earlier steps were skipped, causing position drift
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes("Position") && errorMessage.includes("out of range")) {
      console.warn("[replay] Skipping step with out-of-range position (likely due to earlier skipped steps)", {
        eventIndex,
        stepIndex,
        error: errorMessage,
        stepType: stepJSON.stepType,
        from: stepJSON.from,
        to: stepJSON.to,
        docNodeSize: doc.nodeSize,
      });
      return { doc, stepMap: getSkippedStepMap(step), skipped: true };
    }

    // Handle "Inconsistent open depths" errors - these can happen with complex
    // slice operations when document structure differs from when step was captured
    if (errorMessage.includes("Inconsistent open depths")) {
      console.warn("[replay] Skipping step with inconsistent open depths", {
        eventIndex,
        stepIndex,
        error: errorMessage,
        stepType: stepJSON.stepType,
        from: stepJSON.from,
        to: stepJSON.to,
      });
      return { doc, stepMap: getSkippedStepMap(step), skipped: true };
    }

    // Handle "Invalid content for node" errors - these happen when the step
    // would create content that violates the schema (e.g., text nodes at doc level)
    if (errorMessage.includes("Invalid content for node")) {
      console.warn("[replay] Skipping step that would create invalid content", {
        eventIndex,
        stepIndex,
        error: errorMessage,
        stepType: stepJSON.stepType,
        from: stepJSON.from,
        to: stepJSON.to,
        slice: JSON.stringify(stepJSON.slice).slice(0, 200),
      });
      return { doc, stepMap: getSkippedStepMap(step), skipped: true };
    }

    // Handle "Inserted content deeper than insertion position" errors
    // These happen when a structural step (like list creation) was skipped,
    // and subsequent steps try to insert content that assumes the structure exists
    if (errorMessage.includes("Inserted content deeper than insertion position")) {
      console.warn("[replay] Skipping step with content depth mismatch (likely due to skipped structural step)", {
        eventIndex,
        stepIndex,
        error: errorMessage,
        stepType: stepJSON.stepType,
        from: stepJSON.from,
        to: stepJSON.to,
      });
      return { doc, stepMap: getSkippedStepMap(step), skipped: true };
    }

    // Handle general RangeError (often from slice creation or position mapping)
    if (error instanceof RangeError) {
      console.warn("[replay] Skipping step due to RangeError", {
        eventIndex,
        stepIndex,
        error: errorMessage,
        stepType: stepJSON.stepType,
        from: stepJSON.from,
        to: stepJSON.to,
      });
      return { doc, stepMap: getSkippedStepMap(step), skipped: true };
    }

    // Wrap other errors (deserialization failures, etc.)
    throw new StepApplicationError({
      eventIndex,
      stepIndex,
      stepFailure: error instanceof Error ? error.message : String(error),
      documentStateJSON: doc.toJSON() as Record<string, unknown>,
      failedStepJSON: stepJSON,
    });
  }
}

/**
 * Result from applying all steps in an event.
 */
interface EventStepsResult {
  /** The resulting document after applying all steps */
  doc: ProseMirrorNode;
  /** The cumulative mapping after this event (for transforming subsequent events) */
  mapping: Mapping;
  /** Number of steps that were skipped in this event */
  skippedCount: number;
}

/**
 * Apply all ProseMirror steps from a single event with position mapping.
 *
 * This function handles "position drift" - when earlier steps fail and are skipped,
 * subsequent step positions become invalid because they were calculated for a document
 * state that never existed. We use ProseMirror's Mapping to transform positions.
 *
 * @param doc - Current ProseMirror document
 * @param event - Event containing prosemirrorSteps array
 * @param schema - ProseMirror schema
 * @param eventIndex - Event index (for error reporting)
 * @param cumulativeMapping - Mapping from previous events/steps (for position correction)
 * @returns Result with new document and updated mapping
 * @throws StepApplicationError if a non-recoverable step fails
 */
function applyEventSteps(
  doc: ProseMirrorNode,
  event: ExtensionEditEvent,
  schema: Schema,
  eventIndex: number,
  cumulativeMapping: Mapping = new Mapping()
): EventStepsResult {
  const steps = event.prosemirrorSteps ?? [];

  let currentDoc = doc;
  // Clone the mapping so we can accumulate step maps
  const mapping = new Mapping(cumulativeMapping.maps.slice());
  let skippedCount = 0;

  for (let i = 0; i < steps.length; i++) {
    // Transform step positions using the cumulative mapping from skipped steps
    const transformedStep = transformStepPositions(steps[i], mapping);

    // Apply the transformed step
    const result = applyPMStep(currentDoc, transformedStep, schema, eventIndex, i);

    currentDoc = result.doc;

    if (result.skipped) {
      // Step was skipped - positions may need adjustment for subsequent steps.
      // When a step is skipped, the document structure differs from what the
      // original step sequence expected. We track this with an empty map
      // (the step's intended changes didn't happen).
      skippedCount++;

      // Only add to cumulative mapping when steps are skipped.
      // Skipped steps create position drift that needs correction.
      // For successfully applied steps, subsequent step positions are already
      // relative to the document state after application (no correction needed).
      mapping.appendMap(result.stepMap);
    }
    // Note: Successfully applied steps do NOT add to the mapping.
    // Their positions were captured relative to the post-step document state,
    // so subsequent steps' positions don't need transformation.
  }

  return { doc: currentDoc, mapping, skippedCount };
}

/**
 * Map event type string to ReplayEventType.
 */
function mapEventType(type: string): ReplayEventType {
  const validTypes: ReplayEventType[] = [
    "insert",
    "delete",
    "replace",
    "paste",
    "undo",
    "redo",
  ];
  return validTypes.includes(type as ReplayEventType)
    ? (type as ReplayEventType)
    : "insert";
}

/**
 * Extract deleted text from a document before applying a deletion step.
 *
 * @param doc - The document before the deletion
 * @param from - Start position of deletion
 * @param to - End position of deletion
 * @returns The text that will be deleted, or empty string if extraction fails
 */
function extractDeletedText(
  doc: ProseMirrorNode,
  from: number,
  to: number
): string {
  try {
    // Validate positions
    const maxPos = doc.nodeSize - 1;
    const validFrom = Math.max(0, Math.min(from, maxPos));
    const validTo = Math.max(0, Math.min(to, maxPos));

    if (validFrom >= validTo) {
      return "";
    }

    // Use textBetween with block separator to capture paragraph/block boundaries
    // The third parameter is the block separator (shown between block nodes)
    // The fourth parameter is the leaf text (shown for non-text leaf nodes)
    const text = doc.textBetween(validFrom, validTo, "\n", "");

    // If textBetween returns empty but we have a valid range,
    // it might be a structural element (list item, etc.)
    // Try to get content by slicing the document
    if (!text && validTo > validFrom) {
      const slice = doc.slice(validFrom, validTo);
      const content = slice.content;
      // Try to extract text from the slice
      let sliceText = "";
      content.forEach((node) => {
        if (node.isText) {
          sliceText += node.text ?? "";
        } else if (node.isLeaf) {
          // Non-text leaf node (like hard break)
          sliceText += "\n";
        } else {
          // Block node - extract its text content
          sliceText += node.textContent;
        }
      });
      return sliceText;
    }

    return text;
  } catch (error) {
    console.warn("[replay] Failed to extract deleted text", { from, to, error });
    return "";
  }
}

/**
 * Compute change ranges for a ProseMirror event.
 *
 * Analyzes the steps in an event to determine what regions changed and how.
 * Used for visualization (highlighting additions/deletions in yellow/red).
 *
 * Uses Step.fromJSON and step maps to correctly compute insertion ranges.
 * This is important because inline decorations require from < to.
 *
 * @param event - Event to analyze
 * @param doc - The document BEFORE the event's steps are applied (for extracting deleted text)
 * @param schema - ProseMirror schema for step deserialization
 * @returns Array of change ranges showing additions and deletions
 */
function computePMChangeRanges(
  event: ExtensionEditEvent,
  doc: ProseMirrorNode,
  schema: Schema
): PMChangeRange[] {
  const steps = event.prosemirrorSteps ?? [];
  const ranges: PMChangeRange[] = [];
  const eventType = mapEventType(event.type);
  let currentDoc = doc;

  for (const stepJSON of steps) {
    const stepType = stepJSON.stepType;

    if (stepType === "replace" || stepType === "replaceAround") {
      // Deserialize step to get accurate step map
      let step: Step | null = null;
      try {
        step = Step.fromJSON(schema, stepJSON);
      } catch {
        // Step deserialization failed - skip this step
        continue;
      }

      // Get the step's actual map for accurate position ranges
      const stepMap = step.getMap();

      // Analyze the step map to find insertions and deletions
      stepMap.forEach((oldStart, oldEnd, newStart, newEnd) => {
        const deletedSize = oldEnd - oldStart;
        const insertedSize = newEnd - newStart;

        if (deletedSize > 0) {
          // Deletion - extract text from current doc state
          const deletedText = extractDeletedText(currentDoc, oldStart, oldEnd);

          // Only create a deletion marker if there's actual text content deleted.
          // Structural changes (like wrapping/unwrapping list items) may report
          // deletions in the step map but don't remove visible text content.
          if (deletedText) {
            // Deletion position in the NEW document is at newStart (point position)
            ranges.push({
              type: "deletion",
              from: newStart,
              to: newStart,
              eventType,
              deletedText,
            });
          }
        }

        if (insertedSize > 0) {
          // Insertion - use positions in the NEW document
          ranges.push({
            type: "addition",
            from: newStart,
            to: newEnd,
            eventType,
          });
        }
      });

      // Apply step to update document state for next step
      try {
        const result = step.apply(currentDoc);
        if (result.doc) {
          currentDoc = result.doc;
        }
      } catch {
        // Step failed - continue with current doc
      }
    } else if (stepType === "addMark") {
      // AddMarkStep: formatting addition
      const from = stepJSON.from;
      const to = stepJSON.to;
      const mark = stepJSON.mark as MarkJSON | undefined;

      ranges.push({
        type: "addition",
        from,
        to,
        marks: mark ? [mark] : undefined,
        eventType,
      });
    } else if (stepType === "removeMark") {
      // RemoveMarkStep: formatting removal
      const from = stepJSON.from;
      const to = stepJSON.to;
      const mark = stepJSON.mark as MarkJSON | undefined;

      ranges.push({
        type: "deletion",
        from,
        to,
        marks: mark ? [mark] : undefined,
        eventType,
      });
    }
  }

  return ranges;
}

/**
 * Compute combined change ranges by accumulating steps across a range of events.
 *
 * This function properly handles structured documents (lists, headings, etc.) by:
 * 1. Deserializing each step with Step.fromJSON to get accurate step maps
 * 2. Using the step's actual getMap() method for precise position mapping
 * 3. Mapping ALL previously recorded ranges forward through each new step
 * 4. Merging adjacent ranges at the end
 *
 * The key insight: each step's positions are relative to the document AT THAT POINT.
 * As we process more steps, we must map earlier ranges forward so they remain
 * valid in the final document.
 *
 * @param events - Events in the range to analyze
 * @param eventType - Event type for the change ranges
 * @param schema - ProseMirror schema for deserializing steps
 * @param startingDoc - The document state BEFORE the first event (for extracting deleted text)
 * @returns Array of change ranges with positions in the final document
 */
function computeRangeChangeRangesFromSteps(
  events: ExtensionEditEvent[],
  eventType: ReplayEventType,
  schema: Schema,
  startingDoc: ProseMirrorNode
): PMChangeRange[] {
  const ranges: PMChangeRange[] = [];
  // Track document state as we replay steps (for extracting deleted text)
  let currentDoc = startingDoc;

  for (const event of events) {
    const steps = event.prosemirrorSteps ?? [];

    for (const stepJSON of steps) {
      const stepType = stepJSON.stepType;

      // Deserialize step to get accurate step map
      let step: Step | null = null;
      try {
        step = Step.fromJSON(schema, stepJSON);
      } catch {
        // Step deserialization failed - skip this step
        continue;
      }

      // Get the step's actual map (accurate for all step types)
      const stepMap = step.getMap();

      if (stepType === "replace" || stepType === "replaceAround") {
        // Get insertion info from the step map BEFORE mapping existing ranges
        // This tells us where new content was inserted in the "after" document
        const insertions: Array<{ from: number; to: number }> = [];
        stepMap.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
          const insertedSize = newEnd - newStart;
          if (insertedSize > 0) {
            insertions.push({ from: newStart, to: newEnd });
          }
        });

        // Extract deleted text BEFORE applying the step (using original positions)
        // Store deletion info keyed by newStart position for later use
        const deletions: Array<{ oldStart: number; oldEnd: number; newStart: number; deletedText: string }> = [];
        stepMap.forEach((oldStart, oldEnd, newStart, newEnd) => {
          const deletedSize = oldEnd - oldStart;
          const insertedSize = newEnd - newStart;
          if (deletedSize > 0 && insertedSize === 0) {
            // Extract deleted text from current document state (before step is applied)
            const deletedText = extractDeletedText(currentDoc, oldStart, oldEnd);
            deletions.push({ oldStart, oldEnd, newStart, deletedText });
          }
        });

        // Map all existing ranges forward through this step
        // This ensures earlier ranges have positions valid in the post-step document
        // The mapping will EXPAND ranges when content is inserted inside them
        for (const range of ranges) {
          range.from = stepMap.map(range.from, -1);
          range.to = stepMap.map(range.to, 1);
        }

        // For each insertion, check if it's already covered by an existing range
        // (which would have been expanded by the mapping above)
        // Only add a new range if the insertion is NOT inside an existing range
        for (const ins of insertions) {
          const isCovered = ranges.some(
            (r) => r.type === "addition" && r.from <= ins.from && r.to >= ins.to
          );
          if (!isCovered) {
            ranges.push({
              type: "addition",
              from: ins.from,
              to: ins.to,
              eventType,
            });
          }
        }

        // Add deletion ranges with the extracted deleted text
        for (const del of deletions) {
          ranges.push({
            type: "deletion",
            from: del.newStart,
            to: del.newStart,
            eventType,
            deletedText: del.deletedText,
          });
        }

        // Apply step to update document state for next iteration
        try {
          const result = step.apply(currentDoc);
          if (result.doc) {
            currentDoc = result.doc;
          }
        } catch {
          // Step failed - continue with current doc
        }
      } else if (stepType === "addMark" || stepType === "removeMark") {
        // Marks don't change positions, just record the range
        const from = stepJSON.from;
        const to = stepJSON.to;
        const mark = stepJSON.mark as MarkJSON | undefined;

        ranges.push({
          type: stepType === "addMark" ? "addition" : "deletion",
          from,
          to,
          marks: mark ? [mark] : undefined,
          eventType,
        });
      }
    }
  }

  // Merge adjacent/overlapping ranges of the same type
  return mergeChangeRanges(ranges);
}

/**
 * Tolerance for merging adjacent change ranges.
 *
 * Set to 0 to show separate highlights for each distinct edit location.
 * Higher values merge across ProseMirror structural node boundaries:
 * - Paragraph boundary: ~2 positions (</p><p>)
 * - List item boundary: ~4 positions (</li><li> or </p></li><li><p>)
 * - List to list transition: ~6 positions (</li></ul><ol><li>)
 */
const MERGE_TOLERANCE = 0;

/**
 * Merge adjacent or overlapping change ranges of the same type.
 * This simplifies the visualization by combining contiguous changes.
 *
 * Uses MERGE_TOLERANCE to handle ProseMirror structural node boundaries
 * where position gaps exist between text content in different nodes.
 *
 * Note: Deletion ranges can have from === to (point deletions with deletedText).
 * These should NOT be filtered out or merged.
 */
function mergeChangeRanges(ranges: PMChangeRange[]): PMChangeRange[] {
  if (ranges.length === 0) return [];

  // Filter out invalid ranges (where from > to after mapping)
  // Note: Deletion ranges with from === to are valid (point deletions with deletedText)
  const valid = ranges.filter((r) => r.from <= r.to);
  if (valid.length === 0) return [];

  // Separate deletion ranges with deletedText - these should not be merged
  // (each represents a distinct deleted text at a specific point)
  const deletionsWithText: PMChangeRange[] = [];
  const otherRanges: PMChangeRange[] = [];

  for (const range of valid) {
    if (range.type === "deletion" && range.from === range.to && range.deletedText) {
      // Point deletion with deleted text - keep separate
      deletionsWithText.push(range);
    } else {
      otherRanges.push(range);
    }
  }

  // If only point deletions, return them as-is (sorted by position)
  if (otherRanges.length === 0) {
    return deletionsWithText.sort((a, b) => a.from - b.from);
  }

  // Sort other ranges by position
  const sorted = [...otherRanges].sort((a, b) => a.from - b.from || a.to - b.to);

  const merged: PMChangeRange[] = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];

    // Check if we can merge (same type and within tolerance)
    // Tolerance handles structural node boundaries in ProseMirror documents
    if (next.type === current.type && next.from <= current.to + MERGE_TOLERANCE) {
      // Extend current range
      current.to = Math.max(current.to, next.to);
    } else {
      // Push current and start new
      merged.push(current);
      current = { ...next };
    }
  }

  merged.push(current);

  // Combine merged ranges with point deletions and sort by position
  return [...merged, ...deletionsWithText].sort((a, b) => a.from - b.from);
}

/**
 * Build checkpoints from events at regular intervals.
 *
 * Uses cumulative position mapping to handle "position drift" when steps are skipped.
 * The mapping is tracked across all events so that later steps can have their
 * positions correctly transformed even if earlier steps failed.
 *
 * @param events - Edit events to process
 * @param schema - ProseMirror schema
 * @param interval - Checkpoint interval
 * @param initialDoc - Initial document to start from
 * @returns Array of checkpoints with document state at each interval
 * @throws StepApplicationError if any step fails (stops checkpoint building)
 */
function buildPMCheckpoints(
  events: ExtensionEditEvent[],
  schema: Schema,
  interval: number,
  initialDoc: ProseMirrorNode
): PMDocumentCheckpoint[] {
  const checkpoints: PMDocumentCheckpoint[] = [];
  let doc = initialDoc;
  // Track cumulative position mapping across all events
  let cumulativeMapping = new Mapping();
  let totalSkipped = 0;

  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const steps = event.prosemirrorSteps;
    // Skip events without ProseMirror steps (backward compatibility)
    if (!steps || steps.length === 0) {
      continue;
    }

    // Apply event steps with position mapping (throws on non-recoverable failure)
    const result = applyEventSteps(doc, events[i], schema, i, cumulativeMapping);
    doc = result.doc;
    cumulativeMapping = result.mapping;
    totalSkipped += result.skippedCount;

    // Create checkpoint at intervals (and always at the end)
    const isIntervalBoundary = (i + 1) % interval === 0;
    const isLastEvent = i === events.length - 1;

    if (isIntervalBoundary || isLastEvent) {
      checkpoints.push({
        eventIndex: i,
        docJSON: doc.toJSON() as Record<string, unknown>,
        timestamp: events[i].timestamp,
      });
    }
  }

  // Log summary if any steps were skipped
  if (totalSkipped > 0) {
    console.warn(`[replay] Built ${checkpoints.length} checkpoints, skipped ${totalSkipped} steps due to position/structure issues`);
  }

  return checkpoints;
}

/**
 * Find the nearest checkpoint at or before a target event index.
 */
function findNearestPMCheckpoint(
  checkpoints: PMDocumentCheckpoint[],
  targetIndex: number
): PMDocumentCheckpoint | null {
  let nearest: PMDocumentCheckpoint | null = null;

  for (const checkpoint of checkpoints) {
    if (checkpoint.eventIndex <= targetIndex) {
      nearest = checkpoint;
    } else {
      break; // Checkpoints are sorted
    }
  }

  return nearest;
}

/**
 * Build a ProseMirror document state by applying stored steps from edit events.
 *
 * This reconstructs the exact ProseMirror document at any point in the edit
 * history by replaying ProseMirror steps. It supports:
 *
 * - Full formatting fidelity (bold, italic, headings, etc.)
 * - Checkpointing for fast random access (scrubbing)
 * - LRU caching for smooth playback performance
 * - Error detection (step application failures)
 *
 * **Performance**: With checkpoints every 50 events, seeking to any position
 * requires replaying at most 50 events (~100ms on modern hardware).
 *
 * **Error Handling**: If any step fails to apply, throws StepApplicationError
 * and invalidates all checkpoints. This ensures we never show incorrect state.
 *
 * @param options - Configuration including events, schema, and checkpoint settings
 * @returns A builder object for querying document state at any point
 *
 * @example
 * ```typescript
 * import { schema } from "@markeroo/editor";
 * import { buildPMDocumentState, createInitialDocument } from "@markeroo/replay/core";
 *
 * const initialDoc = createInitialDocument(initialText, schema);
 * const builder = buildPMDocumentState({ events, schema, initialDoc });
 *
 * // Get state at event index 50
 * const state = builder.getStateAtIndex(50);
 * console.log(state.doc.textContent);
 *
 * // Render in an editor
 * <Editor initialContent={state.doc} />
 * ```
 */
export function buildPMDocumentState(
  options: PMDocumentStateBuilderOptions
): PMDocumentStateBuilder {
  const { events, schema, initialDoc, checkpointInterval } = options;

  // Use provided initialDoc or create empty document
  const startingDoc =
    initialDoc ?? schema.node("doc", null, [schema.node("paragraph")]);

  // Preserve server-provided event order (stable sequence for replay fidelity)
  const sortedEvents = [...events];

  // Filter events that have ProseMirror steps (backward compatibility)
  let eventsWithSteps = sortedEvents.filter(
    (e) => e.prosemirrorSteps && e.prosemirrorSteps.length > 0
  );

  // Defensive limit: prevent memory exhaustion with huge edit histories
  if (eventsWithSteps.length > MAX_PM_EVENT_COUNT) {
    console.warn(
      `[replay] PM event count (${eventsWithSteps.length}) exceeds limit, truncating to last ${MAX_PM_EVENT_COUNT} events`
    );
    eventsWithSteps = eventsWithSteps.slice(-MAX_PM_EVENT_COUNT);
  }

  const effectiveInterval =
    checkpointInterval ?? DEFAULT_PM_CHECKPOINT_INTERVAL;

  // Build or use provided checkpoints
  let checkpoints: PMDocumentCheckpoint[];

  try {
    checkpoints =
      options.initialCheckpoints ??
      buildPMCheckpoints(
        eventsWithSteps,
        schema,
        effectiveInterval,
        startingDoc
      );
  } catch (error) {
    // Checkpoint building failed - start with no checkpoints
    console.error("[replay] Failed to build PM checkpoints:", error);
    checkpoints = [];

    // Re-throw if it's a step application error (critical failure)
    if (error instanceof StepApplicationError) {
      throw error;
    }
  }

  // LRU cache for recent states (same pattern as buildDocumentState)
  const stateCache = new Map<number, PMDocumentState>();

  /**
   * Add a state to the LRU cache with eviction.
   */
  function cacheState(index: number, state: PMDocumentState): void {
    if (stateCache.has(index)) {
      stateCache.delete(index);
    }

    if (stateCache.size >= MAX_CHECKPOINT_CACHE_SIZE) {
      const firstKey = stateCache.keys().next().value;
      if (firstKey !== undefined) {
        stateCache.delete(firstKey);
      }
    }

    stateCache.set(index, state);
  }

  /**
   * Build state from a checkpoint by replaying events.
   * Internal helper used by getStateAtIndex.
   *
   * Uses cumulative position mapping to handle "position drift" when steps are skipped.
   */
  function buildStateFromCheckpoint(clampedIndex: number): PMDocumentState {
    const checkpoint = findNearestPMCheckpoint(checkpoints, clampedIndex);

    // Deserialize checkpoint document or use initial doc
    let doc = checkpoint ? schema.nodeFromJSON(checkpoint.docJSON) : startingDoc;

    const startIndex = checkpoint ? checkpoint.eventIndex + 1 : 0;

    // Track cumulative position mapping across events
    let cumulativeMapping = new Mapping();

    // Track the document state BEFORE the target event's steps are applied
    // This is needed for extracting deleted text in computePMChangeRanges
    let docBeforeTargetEvent = doc;

    // Replay events from checkpoint to target index
    for (
      let i = startIndex;
      i <= clampedIndex && i < eventsWithSteps.length;
      i++
    ) {
      // Save doc state before applying the target event
      if (i === clampedIndex) {
        docBeforeTargetEvent = doc;
      }

      try {
        const result = applyEventSteps(doc, eventsWithSteps[i], schema, i, cumulativeMapping);
        doc = result.doc;
        cumulativeMapping = result.mapping;
      } catch (error) {
        // Step application failed - invalidate checkpoints and cache
        checkpoints = [];
        stateCache.clear();

        // Re-throw the error
        throw error;
      }
    }

    // Compute change ranges for visualization (pass doc before event's steps were applied)
    const currentEvent = eventsWithSteps[clampedIndex];
    const changeRanges = currentEvent
      ? computePMChangeRanges(currentEvent, docBeforeTargetEvent, schema)
      : [];

    return {
      doc,
      eventIndex: clampedIndex,
      timestamp: currentEvent?.timestamp ?? null,
      changeRanges,
    };
  }

  function getStateAtIndex(targetIndex: number): PMDocumentState {
    // Handle edge cases
    if (eventsWithSteps.length === 0 || targetIndex < 0) {
      return {
        doc: startingDoc,
        eventIndex: -1,
        timestamp: null,
        changeRanges: [],
      };
    }

    const clampedIndex = Math.min(targetIndex, eventsWithSteps.length - 1);

    // Check cache
    const cached = stateCache.get(clampedIndex);
    if (cached) {
      // Move to end for LRU ordering
      stateCache.delete(clampedIndex);
      stateCache.set(clampedIndex, cached);
      return cached;
    }

    // Build state from checkpoint (may throw)
    const state = buildStateFromCheckpoint(clampedIndex);

    // Cache for future access
    cacheState(clampedIndex, state);

    return state;
  }

  function getStateAtTimestamp(timestamp: string): PMDocumentState {
    if (eventsWithSteps.length === 0) {
      return {
        doc: startingDoc,
        eventIndex: -1,
        timestamp: null,
        changeRanges: [],
      };
    }

    const targetTime = new Date(timestamp).getTime();

    if (isNaN(targetTime)) {
      return getStateAtIndex(eventsWithSteps.length - 1);
    }

    // Binary search
    let low = 0;
    let high = eventsWithSteps.length - 1;
    let result = -1;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const eventTime = new Date(eventsWithSteps[mid].timestamp).getTime();

      if (eventTime <= targetTime) {
        result = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    return getStateAtIndex(result);
  }

  /**
   * Get document state for a range of events, computing the combined diff.
   *
   * This builds the document at the end of the range and computes change ranges
   * by analyzing the actual ProseMirror steps in the events. This properly handles
   * structured content (lists, headings, etc.) where text-based diffing would fail.
   *
   * @param startIndex - First event index (inclusive)
   * @param endIndex - Last event index (inclusive)
   * @param eventType - Event type for the change ranges (defaults to 'insert')
   * @returns Document state at endIndex with changeRanges showing combined diff
   */
  function getStateForRange(
    startIndex: number,
    endIndex: number,
    eventType: ReplayEventType = "insert"
  ): PMDocumentState {
    // Handle edge cases
    if (eventsWithSteps.length === 0) {
      return {
        doc: startingDoc,
        eventIndex: -1,
        timestamp: null,
        changeRanges: [],
      };
    }

    // Clamp indices to valid range
    const effectiveStart = Math.max(
      0,
      Math.min(startIndex, eventsWithSteps.length - 1)
    );
    const effectiveEnd = Math.max(
      effectiveStart,
      Math.min(endIndex, eventsWithSteps.length - 1)
    );

    // If it's a single event, use the regular getStateAtIndex
    if (effectiveStart === effectiveEnd) {
      return getStateAtIndex(effectiveEnd);
    }

    // Build doc at the end of the range
    const endState = getStateAtIndex(effectiveEnd);

    // Get the document state BEFORE the first event in the range
    // This is needed for extracting deleted text in computeRangeChangeRangesFromSteps
    const stateBeforeRange = effectiveStart > 0
      ? getStateAtIndex(effectiveStart - 1)
      : { doc: startingDoc, eventIndex: -1, timestamp: null, changeRanges: [] };

    // Extract events in the range
    const rangeEvents = eventsWithSteps.slice(effectiveStart, effectiveEnd + 1);

    // Compute change ranges from actual steps (handles structural content correctly)
    const changeRanges = computeRangeChangeRangesFromSteps(
      rangeEvents,
      eventType,
      schema,
      stateBeforeRange.doc
    );

    return {
      doc: endState.doc,
      eventIndex: effectiveEnd,
      timestamp: eventsWithSteps[effectiveEnd]?.timestamp ?? null,
      changeRanges,
    };
  }

  function getCheckpoints(): PMDocumentCheckpoint[] {
    return [...checkpoints];
  }

  function getTotalEvents(): number {
    return eventsWithSteps.length;
  }

  function getTimestampRange(): { start: string; end: string } | null {
    if (eventsWithSteps.length === 0) return null;
    return {
      start: eventsWithSteps[0].timestamp,
      end: eventsWithSteps[eventsWithSteps.length - 1].timestamp,
    };
  }

  return {
    getStateAtIndex,
    getStateAtTimestamp,
    getStateForRange,
    getCheckpoints,
    getTotalEvents,
    getTimestampRange,
  };
}
